import React, { FC, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { CoreUtils } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { PlayerPositionLong } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { handleSpaceOrEnterPress, parseValueFromLocalizedAttribute } from '~/core/utils';

import { PregameComponentFlags } from '../../sections/landing/components/preGame/utils';
import { SectionHeader, SelectorContainer, SelectorItem, StatDivider } from '../../styles';
import {
    AwayAndHomeTeam,
    CommonGCGame,
    ContextDescriptor,
    Goalie,
    GoalieSeasonStat,
    Skater,
    SkaterSeasonStat,
    TeamGameStats,
} from '../../types';
import { ContextSubHeader } from '../playersToWatch/components/header/CompareHeader';

import { LeftShadowTable } from './LeftShadowTable';
import { goalieColumns } from './goalieColumns';
import { skaterColumns } from './skaterColumns';

import { SubHeader, RosterHeader, TeamRosterContainer, HeaderContainer } from './styles';

export type PlayersByPosition = {
    forwards: Array<SkaterSeasonStat>;
    defense: Array<SkaterSeasonStat>;
    goalies: Array<GoalieSeasonStat>;
};

type Props = {
    contextDescriptor?: ContextDescriptor;
    game: CommonGCGame;
    getPlayers?: (teamId: number) => PlayersByPosition;
    playerByGameStats?: AwayAndHomeTeam<TeamGameStats>;
    pregameComponentFlags?: PregameComponentFlags;
};

const TeamRoster: FC<Props> = ({ contextDescriptor, game, getPlayers, playerByGameStats, pregameComponentFlags }) => {
    const { homeTeam, awayTeam, season } = game;
    // we define isWebview up here rather than in LeftShadowTable to save performance by making only one call to the useIsWebview hook rather than three
    const intl = useIntl();
    const isWebview = useIsWebview();

    const [visibleTeam, setVisibleTeam] = useState<number>(awayTeam.id);

    const isHomeTeam = game.homeTeam.id === visibleTeam;
    const isPregameState = CoreUtils.isPregame(game.gameState);
    const rosterInitialSort = isPregameState ? [{ id: 'gamesPlayed', desc: true }] : [];

    let forwards: Array<SkaterSeasonStat | Skater> = [];
    let defense: Array<SkaterSeasonStat | Skater> = [];
    let goalies: Array<GoalieSeasonStat | Goalie> = [];

    if (isPregameState && getPlayers) {
        const players = getPlayers(visibleTeam);
        defense = players.defense;
        forwards = players.forwards;
        goalies = players.goalies;
    } else if (!isPregameState && isHomeTeam && playerByGameStats) {
        const { homeTeam } = playerByGameStats;
        [forwards, defense, goalies] = [homeTeam.forwards, homeTeam.defense, homeTeam.goalies];
    } else if (!isPregameState && !isHomeTeam && playerByGameStats) {
        const { awayTeam } = playerByGameStats;
        [forwards, defense, goalies] = [awayTeam.forwards, awayTeam.defense, awayTeam.goalies];
    }

    const Title = () =>
        isPregameState ? (
            <HeaderContainer>
                <SectionHeader aria-level={2}>
                    <FormattedMessage {...CommonMessages.tableHeaders.long.roster} />
                </SectionHeader>
                <SubHeader>
                    <ContextSubHeader contextDescriptor={contextDescriptor} currentSeason={season} />
                </SubHeader>
            </HeaderContainer>
        ) : (
            <SectionHeader aria-level={2}>
                <FormattedMessage
                    defaultMessage="Game Sheet"
                    description="A header describing a Game Sheet component"
                />
            </SectionHeader>
        );

    return (
        <TeamRosterContainer>
            {isPregameState && !pregameComponentFlags?.isTeamRosterFirstVisible && <StatDivider />}
            <RosterHeader>
                <Title />
                <SelectorContainer>
                    <SelectorItem
                        role="button"
                        tabIndex={0}
                        onClick={() => setVisibleTeam(awayTeam.id)}
                        onKeyPress={(e) => handleSpaceOrEnterPress(e, () => setVisibleTeam(awayTeam.id))}
                        $isSelected={awayTeam.id === visibleTeam}
                    >
                        {parseValueFromLocalizedAttribute(awayTeam.commonName)}
                    </SelectorItem>
                    <SelectorItem
                        role="button"
                        tabIndex={0}
                        onClick={() => setVisibleTeam(homeTeam.id)}
                        onKeyPress={(e) => handleSpaceOrEnterPress(e, () => setVisibleTeam(homeTeam.id))}
                        $isSelected={homeTeam.id === visibleTeam}
                    >
                        {parseValueFromLocalizedAttribute(homeTeam.commonName)}
                    </SelectorItem>
                </SelectorContainer>
            </RosterHeader>
            <LeftShadowTable
                data={forwards}
                initialSort={rosterInitialSort}
                columns={skaterColumns(
                    PlayerPositionLong.forwards,
                    isPregameState,
                    game.limitedScoring,
                    isWebview,
                    intl
                )}
            />
            <LeftShadowTable
                data={defense}
                initialSort={rosterInitialSort}
                columns={skaterColumns(
                    PlayerPositionLong.defensemen,
                    isPregameState,
                    game.limitedScoring,
                    isWebview,
                    intl
                )}
            />
            <LeftShadowTable
                data={goalies}
                initialSort={rosterInitialSort}
                columns={goalieColumns(
                    PlayerPositionLong.goalies,
                    isPregameState,
                    game.limitedScoring,
                    isWebview,
                    intl
                )}
            />
        </TeamRosterContainer>
    );
};
export default TeamRoster;
